import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2"; // Import SweetAlert2
import "./ManageSubscriptionPrice.css";
import API_URL from '../../api/ExamCalendarApi';

const ManageSubscriptionPriceDetails = () => {
  const [exams, setExams] = useState([]);
  const [examDetails, setExamDetails] = useState([
    {
      examName: "",
      priceType: "month",
      pricingDetails: [
        {
          duration: "",
          price: "",
        },
      ],
    },
  ]);
  const [editingExamIndex, setEditingExamIndex] = useState(null);
  const [selectedExamIds, setSelectedExamIds] = useState([]);
  const [examNames, setExamNames] = useState([]);


  // Fetch exams and exam names when the component mounts
  useEffect(() => {
    fetchExams();
    fetchExamNames();
  }, []);

  // Fetch all exams
  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscription/exams`);
      setExams(response.data);
    } catch (error) {
      console.error("Error fetching exams:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch exams",
        icon: "error",
      });
    }
  };

  // Fetch all exam names
  const fetchExamNames = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/CodeFormat/get-all-examCodeData`
      );
      setExamNames(response.data.map((exam) => exam.examName));
    } catch (error) {
      console.error("Error fetching exam names:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch exam names",
        icon: "error",
      });
    }
  };

  // Add a new exam
  const addExam = () => {
    setExamDetails([
      ...examDetails,
      {
        examName: "",
        priceType: "month",
        pricingDetails: [
          {
            duration: "",
            price: "",
          },
        ],
      },
    ]);
  };
  const removeExam = (examIndex) => {
    const updatedExams = examDetails.filter((_, index) => index !== examIndex);
    setExamDetails(updatedExams);
  };

  // Update exam name
  const updateExamName = (examIndex, name) => {
    const newExamDetails = [...examDetails];
    newExamDetails[examIndex].examName = name;
    setExamDetails(newExamDetails);
  };

  // Change price type (month/day)
  const changePriceType = (examIndex, type) => {
    const newExamDetails = [...examDetails];
    newExamDetails[examIndex].priceType = type;
    newExamDetails[examIndex].pricingDetails = [
      {
        duration: "",
        price: "",
      },
    ];
    setExamDetails(newExamDetails);
  };

  // Add pricing detail
  const addPricingDetail = (examIndex) => {
    const newExamDetails = [...examDetails];
    newExamDetails[examIndex].pricingDetails.push({
      duration: "",
      price: "",
    });
    setExamDetails(newExamDetails);
  };

  // Update pricing detail
  const updatePricingDetail = (examIndex, detailIndex, field, value) => {
    const newExamDetails = [...examDetails];
    newExamDetails[examIndex].pricingDetails[detailIndex][field] = value;
    setExamDetails(newExamDetails);
  };

  // Remove pricing detail
  const removePricingDetail = (examIndex, detailIndex) => {
    const newExamDetails = [...examDetails];
    newExamDetails[examIndex].pricingDetails.splice(detailIndex, 1);
    setExamDetails(newExamDetails);
  };

  // Set editing mode for a specific exam
  const startEditing = (examIndex) => {
    setEditingExamIndex(examIndex);
    const examToEdit = exams[examIndex];
    const updatedExamDetails = [
      {
        examName: examToEdit.examName,
        priceType: examToEdit.priceType || "month",
        pricingDetails: examToEdit.prices.map((priceObj) => ({
          duration: priceObj.months.replace(" months", "").replace(" days", ""),
          price: priceObj.price.toString(),
        })),
      },
    ];
    setExamDetails(updatedExamDetails);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setTimeout(() => {
      const inputs = document.querySelectorAll(
        ".admin-managesubscription-form input"
      );
      inputs.forEach((input) => {
        input.classList.add("sparkle-input");
      });

      setTimeout(() => {
        inputs.forEach((input) => {
          input.classList.remove("sparkle-input");
        });
      }, 1000);
    }, 500);
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = examDetails.every(
      (exam) =>
        exam.examName &&
        exam.pricingDetails.every((detail) => detail.duration && detail.price)
    );

    if (!isValid) {
      Swal.fire({
        title: "Error",
        text: "Please fill all exam and pricing details",
        icon: "error",
      });
      return;
    }

    try {
      const formattedExams = examDetails.map((exam) => ({
        examName: exam.examName,
        prices: Object.fromEntries(
          exam.pricingDetails.map((detail) => [
            `${detail.duration}${
              exam.priceType === "month" ? " months" : " days"
            }`,
            parseFloat(detail.price),
          ])
        ),
      }));

      if (editingExamIndex !== null) {
        await axios.put(
          `${API_URL}/subscription/edit-subscription/${exams[editingExamIndex]._id}`,
          {
            examName: formattedExams[0].examName,
            prices: formattedExams[0].prices,
          }
        );
        Swal.fire({
          title: "Success",
          text: "Exam updated successfully",
          icon: "success",
        });
      } else {
        await axios.post(`${API_URL}/subscription/add-subscriptionPrice`, {
          exams: formattedExams,
        });
        Swal.fire({
          title: "Success",
          text: "Exams added successfully",
          icon: "success",
        });
      }

      fetchExams();
      setEditingExamIndex(null);
      setExamDetails([
        {
          examName: "",
          priceType: "month",
          pricingDetails: [{ duration: "", price: "" }],
        },
      ]);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Error saving exams",
        icon: "error",
      });
      console.error(error);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingExamIndex(null);
    setExamDetails([
      {
        examName: "",
        priceType: "month",
        pricingDetails: [{ duration: "", price: "" }],
      },
    ]);
  };

  // Delete an exam by ID or multiple exams
  const deleteExams = async () => {
    try {
      if (selectedExamIds.length === 0) {
        Swal.fire({
          title: "Error",
          text: "Please select exams to delete",
          icon: "error",
        });
        return;
      }

      await axios.delete(`${API_URL}/subscription/exams`, {
        data: { ids: selectedExamIds },
      });

      Swal.fire({
        title: "Success",
        text: "Exam(s) deleted successfully",
        icon: "success",
      });

      setSelectedExamIds([]);
      fetchExams();
    } catch (error) {
      console.error("Full error details:", error.response || error);
      Swal.fire({
        title: "Error",
        text: "Error deleting exam(s)",
        icon: "error",
      });
    }
  };

  // Handle checkbox selection for exams
  const handleExamSelect = (examId) => {
    setSelectedExamIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(examId)) {
        return prevSelectedIds.filter((id) => id !== examId);
      } else {
        return [...prevSelectedIds, examId];
      }
    });
  };

  return (
    <div className="admin-managesubscription-container">
      <div className="admin-managesubscription-content">
        <h2>
          {editingExamIndex !== null
            ? "Edit Exam Subscription"
            : "Manage Exam Subscriptions"}
        </h2>

        <form onSubmit={handleSubmit} className="admin-managesubscription-form">
  {examDetails.map((exam, examIndex) => (
    <div key={examIndex} className="admin-managesubscription-card">
      <div className="admin-managesubscription-card-header">
        <div className="admin-managesubscription-form-group">
          <select
            value={exam.examName}
            onChange={(e) => updateExamName(examIndex, e.target.value)}
          >
            <option value="">Select Exam</option>
            {examNames.map((examName, index) => (
              <option key={index} value={examName}>
                {examName}
              </option>
            ))}
          </select>
          {/* Conditionally show the Remove button only if there is more than one exam */}
          {examDetails.length > 1 && (
            <button
              type="button"
              onClick={() => removeExam(examIndex)}
              className="admin-managesubscription-remove-btn"
            >
              Remove
            </button>
          )}
        </div>
        <div className="admin-managesubscription-form-group">
          <select
            value={exam.priceType}
            onChange={(e) => changePriceType(examIndex, e.target.value)}
          >
            <option value="month">Month</option>
            <option value="day">Day</option>
          </select>
        </div>
      </div>
      <div className="admin-managesubscription-card-body">
        {exam.pricingDetails.map((detail, detailIndex) => (
          <div
            key={detailIndex}
            className="admin-managesubscription-pricing-detail"
          >
            <input
              type="number"
              placeholder="Duration"
              value={detail.duration}
              onChange={(e) =>
                updatePricingDetail(
                  examIndex,
                  detailIndex,
                  "duration",
                  e.target.value
                )
              }
            />
            <input
              type="number"
              placeholder="Price"
              value={detail.price}
              onChange={(e) =>
                updatePricingDetail(
                  examIndex,
                  detailIndex,
                  "price",
                  e.target.value
                )
              }
            />
            <button
              type="button"
              onClick={() =>
                removePricingDetail(examIndex, detailIndex)
              }
              className="admin-managesubscription-remove-pricing"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addPricingDetail(examIndex)}
          className="admin-managesubscription-add-pricing"
        >
          + Add Pricing
        </button>
      </div>
    </div>
  ))}

  <div className="admin-managesubscription-buttons">
    {editingExamIndex !== null ? (
      <>
        <button
          type="button"
          onClick={cancelEdit}
          className="admin-managesubscription-btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="admin-managesubscription-btn-primary"
        >
          Update Exam
        </button>
      </>
    ) : (
      <>
        <button
          type="button"
          onClick={addExam}
          className="admin-managesubscription-btn-secondary"
        >
          + Add Another Exam
        </button>
        <button
          type="submit"
          className="admin-managesubscription-btn-primary"
        >
          Submit Exams
        </button>
      </>
    )}
  </div>
</form>


        <h3 className="admin-managesubscription-existing-exams">
          Existing Exams
        </h3>
        <div className="admin-managesubscription-table-wrapper">
          <table className="admin-managesubscription-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedExamIds.length === exams.length}
                    onChange={() =>
                      setSelectedExamIds(
                        selectedExamIds.length === exams.length
                          ? []
                          : exams.map((exam) => exam._id)
                      )
                    }
                  />
                </th>
                <th>Exam Name</th>
                <th>Prices</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exams.map((exam) => (
                <tr key={exam._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedExamIds.includes(exam._id)}
                      onChange={() => handleExamSelect(exam._id)}
                    />
                  </td>
                  <td>{exam.examName}</td>
                  <td>
                    {exam.prices && exam.prices.length > 0
                      ? exam.prices
                          .map(
                            (priceObj) =>
                              `${priceObj.months}: ₹${priceObj.price}`
                          )
                          .join(", ")
                      : "No prices available"}
                  </td>
                  <td>
                    <button
                      onClick={() => startEditing(exams.indexOf(exam))}
                      className="admin-managesubscription-btn-warning"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelectedExamIds([exam._id]);
                        deleteExams();
                      }}
                      className="admin-managesubscription-btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageSubscriptionPriceDetails;
